import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import risklocatorServices from '../services/risklocatorServices';
import { ApiState } from '../constants/enums';
import { IRecursiveQuestion, IRecursiveQuestionResponse, IResults, IResultsResponse } from '../types/risklocator.type';

export interface IErrorObject {
    errorCode?: string;
    errorMessage?: string;
}
export interface RiskLocatorState {
    apiState: {
        getRecursiveQuestion: ApiState;
        getResultsForRecursiveQuest: ApiState;
    };
    recursiveQuestion: IRecursiveQuestion[];
    results: IResults[];
    selectedAnswers: string[];
    error: IErrorObject;
}
export const initialRecursiveData = [
    {
        questionNodeId: 0,
        questionCode: '',
        questionText: '',
        optionType: '',
        answerGroupId: 0,
        options: [
            {
                answerText: '',
                answerCode: '',
            },
        ],
    },
];

const initialState: RiskLocatorState = {
    apiState: {
        getRecursiveQuestion: ApiState.NOT_STARTED,
        getResultsForRecursiveQuest: ApiState.NOT_STARTED,
    },
    recursiveQuestion: [],
    results: [],
    selectedAnswers: [],
    error: {
        errorCode: '',
        errorMessage: '',
    },
};

export const getRecursiveQuestionsForRiskLocator = createAsyncThunk<
    IRecursiveQuestionResponse,
    { questionNodeId?: number; answerCode?: string; riskType?: string; answerGroupId?: number }
>('risk-locator/get-recursive-question', async ({ questionNodeId, answerCode, riskType, answerGroupId }) => {
    return risklocatorServices
        .getRecursiveQuestions(questionNodeId, answerCode, riskType, answerGroupId)
        .then((res) => {
            return res;
        });
});

export const getResultsForRecursiveQuest = createAsyncThunk<
    IResultsResponse,
    { insuranceType: string; params: Record<string, string> }
>('risk-locator/get-results', async ({ insuranceType, params }) => {
    return risklocatorServices.getResultsForRiskLocator(insuranceType, params).then((res) => {
        return res;
    });
});
const riskLocatorSlice = createSlice({
    name: 'risklocator',
    initialState,
    extraReducers(builder) {
        builder
            .addCase(getRecursiveQuestionsForRiskLocator.pending, (state) => {
                state.apiState.getRecursiveQuestion = ApiState.PENDING;
            })
            .addCase(
                getRecursiveQuestionsForRiskLocator.fulfilled,
                (state, action: PayloadAction<IRecursiveQuestionResponse>) => {
                    state.recursiveQuestion = [...state.recursiveQuestion, action.payload.data];
                    state.apiState.getRecursiveQuestion = ApiState.FULFILLED;
                },
            )
            .addCase(getRecursiveQuestionsForRiskLocator.rejected, (state) => {
                state.apiState.getRecursiveQuestion = ApiState.REJECTED;
            })
            .addCase(getResultsForRecursiveQuest.pending, (state) => {
                state.apiState.getResultsForRecursiveQuest = ApiState.PENDING;
            })
            .addCase(getResultsForRecursiveQuest.fulfilled, (state, action: PayloadAction<IResultsResponse>) => {
                state.results = [action.payload.data];
                state.apiState.getResultsForRecursiveQuest = ApiState.FULFILLED;
            })
            .addCase(getResultsForRecursiveQuest.rejected, (state, action) => {
                state.results = [];
                state.apiState.getResultsForRecursiveQuest = ApiState.REJECTED;
                state.error.errorCode = action.error.code;
                state.error.errorMessage = action.error.message;
            });
    },
    reducers: {
        setRecursiveQuestions(state, action: PayloadAction<number>) {
            state.recursiveQuestion = state.recursiveQuestion.slice(0, action.payload + 1);
        },
        resetRecursiveQuestion(state) {
            state.recursiveQuestion = [];
            state.selectedAnswers = [];
        },
        setSelectedAnswers(state, action: PayloadAction<string[]>) {
            state.selectedAnswers = action.payload;
        },
        resetResults(state) {
            state.results = [];
        },
    },
});

export const { setRecursiveQuestions, resetRecursiveQuestion, setSelectedAnswers, resetResults } =
    riskLocatorSlice.actions;

export default riskLocatorSlice.reducer;
